// src/components/news/NewsCommentItem.js

import React, {
  useMemo,
  useState,
} from 'react';

import {
  Link,
} from 'react-router-dom';


import placeholderProfile from '../../assets/placeholder-profile.png';
import goldTickIcon from '../../assets/goldtick-icon.png';

import NewsCommentReactions from './NewsCommentReactions';

import './NewsCommentItem.css';

function getCommentId(comment) {
  return String(
    comment?.id ||
    comment?._id ||
    ''
  );
}

function buildReplyDisplayOrder(
  replies,
  rootCommentId
) {
  if (!Array.isArray(replies)) {
    return [];
  }

  const childrenByParent =
    new Map();

  for (const reply of replies) {
    const parentId =
      reply?.parentCommentId
        ? String(reply.parentCommentId)
        : rootCommentId;

    if (!childrenByParent.has(parentId)) {
      childrenByParent.set(parentId, []);
    }

    childrenByParent
      .get(parentId)
      .push(reply);
  }

  const orderedReplies = [];
  const visited = new Set();

  const appendChildren =
    (parentId) => {
      const children =
        childrenByParent.get(parentId) ||
        [];

      for (const child of children) {
        const childId =
          getCommentId(child);

        if (
          !childId ||
          visited.has(childId)
        ) {
          continue;
        }

        visited.add(childId);
        orderedReplies.push(child);

        appendChildren(childId);
      }
    };

  appendChildren(rootCommentId);

  /*
   * Safety fallback:
   * never hide replies if old/corrupt data has
   * a missing parent reference.
   */
  for (const reply of replies) {
    const replyId =
      getCommentId(reply);

    if (
      replyId &&
      !visited.has(replyId)
    ) {
      orderedReplies.push(reply);
    }
  }

  return orderedReplies;
}

function getTimeAgo(value) {
  if (!value) {
    return '';
  }

  let timestamp = value;

  if (
    typeof timestamp === 'number' &&
    String(timestamp).length === 10
  ) {
    timestamp *= 1000;
  }

  const date = new Date(timestamp);

  if (
    Number.isNaN(date.getTime())
  ) {
    return '';
  }

  const now = new Date();

  let difference =
    now.getTime() -
    date.getTime();

  if (difference < 0) {
    difference = 0;
  }

  const seconds =
    Math.floor(
      difference / 1000
    );

  if (seconds < 60) {
    return 'Just now';
  }

  const minutes =
    Math.floor(
      seconds / 60
    );

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours =
    Math.floor(
      minutes / 60
    );

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days =
    Math.floor(
      hours / 24
    );

  if (days <= 7) {
    return `${days}d ago`;
  }

  if (days < 365) {
    try {
      return new Intl.DateTimeFormat(
        undefined,
        {
          day: 'numeric',
          month: 'short',
        }
      ).format(date);
    } catch {
      return date.toLocaleDateString();
    }
  }

  const years =
    Math.floor(
      days / 365
    );

  return years === 1
    ? '1 year ago'
    : `${years} years ago`;
}

function isAdminAuthor(author) {
  return (
    author?.isAdmin === true ||
    Boolean(author?.adminRole)
  );
}

function isAppAuthor(author) {
  return (
    author?.isApp === true ||
    author?.accountType === 'app'
  );
}

function isSpecialAuthor(author) {
  return (
    isAdminAuthor(author) ||
    isAppAuthor(author)
  );
}

function getSpecialTitle(author) {
  if (isAdminAuthor(author)) {
    return (
      author?.adminRole ||
      'Admin'
    );
  }

  if (isAppAuthor(author)) {
    return 'App account';
  }

  return '';
}

function normalizeAuthor(
  item
) {
  const author =
    item?.author &&
    typeof item.author === 'object'
      ? item.author
      : {};

  return {
    _id:
      author._id ||
      item?.authorId ||
      null,

    name:
      author.name ||
      item?.username ||
      'BarterFi User',

    username:
      author.username ||
      item?.username ||
      '',

    profilePic:
      author.profilePic ||
      placeholderProfile,

    userType:
      author.userType ||
      'pioneer',

    isMerchant:
      Boolean(
        author.isMerchant
      ),

    adminRole:
      author.adminRole ||
      null,

    isAdmin:
      isAdminAuthor(author),

    accountType:
      author.accountType ||
      'primary',

    isApp:
      isAppAuthor(author),
  };
}

function AuthorMeta({
  item,
  compact = false,
}) {
  const author =
    normalizeAuthor(item);

  const timestamp =
    getTimeAgo(
      item?.createdAt ||
      item?.updatedAt
    );

  const username =
    author.username;

  return (
    <div
      className={`news-comment-author-meta ${
        compact
          ? 'news-comment-author-meta--compact'
          : ''
      }`}
    >
      {username ? (
        <Link
          to={`/user/${username}`}
          className="news-comment-author-link"
        >

<span className="news-comment-author-name">
  {author.name ||
    username ||
    'BarterFi User'}
</span>

          {isSpecialAuthor(
            author
          ) && (
            <img
              src={goldTickIcon}
              alt={
                isAdminAuthor(
                  author
                )
                  ? 'Admin verified'
                  : 'App verified'
              }
              title={getSpecialTitle(
                author
              )}
              className="news-comment-author-tick"
            />
          )}
        </Link>
      ) : (
        <span className="news-comment-author-link">
          <span className="news-comment-author-name">
            {author.name}
          </span>
        </span>
      )}


      {timestamp && (
        <>
          <span
            className="news-comment-meta-dot"
            aria-hidden="true"
          >
            •
          </span>

          <span className="news-comment-time">
            {timestamp}
          </span>
        </>
      )}
    </div>
  );
}


function NewsReplyItem({
  reply,
  onReply,
  onDelete,
  onReact,
  deletingId,
  reactingId = '',
  replyTargetId = '',
}) {


  const replyId =
    getCommentId(reply);

  const author =
    normalizeAuthor(reply);

  const isDeleted =
    reply?.isDeleted ||
    reply?.status === 'deleted';

  const deleting =
    deletingId === replyId;

const reacting =
  reactingId === replyId;


const isReplyTarget =
  Boolean(replyId) &&
  replyId === replyTargetId;


const parentCommentId =
  reply?.parentCommentId
    ? String(reply.parentCommentId)
    : '';

const rootCommentId =
  reply?.rootCommentId
    ? String(reply.rootCommentId)
    : '';

const isNestedReply =
  Boolean(parentCommentId) &&
  Boolean(rootCommentId) &&
  parentCommentId !== rootCommentId;

const replyingToName =
  reply?.replyingTo?.name ||
  reply?.replyingTo?.username ||
  '';



  return (

<div
  className={`news-comment-reply-item ${
    isReplyTarget
      ? 'news-comment-reply-item--reply-target'
      : ''
  }`}
  data-news-reply-target-id={replyId}
>

      <div className="news-comment-reply-connector" />

      <img
        src={
          author.profilePic ||
          placeholderProfile
        }
        alt=""
        className="news-comment-reply-avatar"
        onError={(event) => {
          event.currentTarget.src =
            placeholderProfile;
        }}
      />

      <div className="news-comment-reply-content">


<AuthorMeta
  item={reply}
  compact
/>


{isNestedReply && replyingToName && (
  <div className="news-comment-replying-to">
    Replying to{' '}
    <strong>
      {replyingToName}
    </strong>
  </div>
)}

<p
  className={`news-comment-text news-comment-text--reply ${
    isDeleted
      ? 'news-comment-text--deleted'
      : ''
  }`}
>




          {isDeleted
            ? 'This reply was deleted.'
            : reply?.text}
        </p>




{!isDeleted && (
  <div className="news-comment-actions">
    <NewsCommentReactions
      reactionSummary={
        reply?.reactionSummary ||
        []
      }
      viewerReaction={
        reply?.viewerReaction ||
        null
      }
      onReact={(reactionType) =>
        onReact?.(
          reply,
          reactionType
        )
      }
      disabled={reacting}
      compact
    />

    <button
      type="button"
      className="news-comment-action-btn"
      onClick={() =>
        onReply?.(reply)
      }
    >
      Reply
    </button>

    {reply?.canDelete && (
      <button
        type="button"
        className="news-comment-action-btn news-comment-action-btn--delete"
        disabled={deleting}
        onClick={() =>
          onDelete?.(reply)
        }
      >
        {deleting
          ? 'Deleting…'
          : 'Delete'}
      </button>
    )}
  </div>
)}




      </div>
    </div>
  );
}



function NewsCommentItem({
  comment,
  replyTarget = null,
  onStartReply,
  onDelete,
  onReact,
  deletingId = '',
  reactingId = '',
}) {



const [
  repliesExpanded,
  setRepliesExpanded,
] = useState(false);

  const commentId =
    getCommentId(comment);

const replyTargetId =
  getCommentId(replyTarget);

const isCommentReplyTarget =
  Boolean(commentId) &&
  commentId === replyTargetId;


  const author =
    normalizeAuthor(
      comment
    );

  const replies =
    useMemo(() => {
      return Array.isArray(
        comment?.replies
      )
        ? comment.replies
        : [];
    }, [comment?.replies]);


const displayReplies =
  useMemo(() => {
    return buildReplyDisplayOrder(
      replies,
      commentId
    );
  }, [
    replies,
    commentId,
  ]);


  const visibleReplyCount =
    replies.length;

  const storedReplyCount =
    Number(
      comment?.replyCount ||
      0
    );

  const replyCount =
    Math.max(
      visibleReplyCount,
      storedReplyCount
    );

  const isDeleted =
    comment?.isDeleted ||
    comment?.status ===
      'deleted';

  const deleting =
    deletingId === commentId;

const reacting =
  reactingId === commentId;



const handleOpenReply =
  (target = comment) => {
    const targetIsDeleted =
      target?.isDeleted ||
      target?.status === 'deleted';

    if (targetIsDeleted) {
      return;
    }

    setRepliesExpanded(true);
    onStartReply?.(target);
  };


  return (



<article
  className={`news-comment-item ${
    isDeleted
      ? 'news-comment-item--deleted'
      : 'news-comment-item--active'
  } ${
    replyCount > 0
      ? 'news-comment-item--has-replies'
      : 'news-comment-item--no-replies'
  } ${
    isCommentReplyTarget
      ? 'news-comment-item--reply-target'
      : ''
  }`}
  data-comment-id={commentId}
  data-news-reply-target-id={commentId}
>


      <div className="news-comment-main-row">
        <img
          src={
            author.profilePic ||
            placeholderProfile
          }
          alt=""
          className="news-comment-avatar"
          onError={(event) => {
            event.currentTarget.src =
              placeholderProfile;
          }}
        />

        <div className="news-comment-content">
          <AuthorMeta
            item={comment}
          />

          <p
            className={`news-comment-text ${
              isDeleted
                ? 'news-comment-text--deleted'
                : ''
            }`}
          >
            {isDeleted
              ? 'This comment was deleted.'
              : comment?.text}
          </p>







{!isDeleted && (
  <div className="news-comment-actions">
    <NewsCommentReactions
      reactionSummary={
        comment?.reactionSummary ||
        []
      }
      viewerReaction={
        comment?.viewerReaction ||
        null
      }
      onReact={(reactionType) =>
        onReact?.(
          comment,
          reactionType
        )
      }
      disabled={reacting}
    />

    <button
      type="button"
      className="news-comment-action-btn"
      onClick={() =>
        handleOpenReply(
          comment
        )
      }
    >
      Reply
    </button>

    {comment?.canDelete && (
      <button
        type="button"
        className="news-comment-action-btn news-comment-action-btn--delete"
        disabled={
          deleting
        }
        onClick={() =>
          onDelete?.(
            comment
          )
        }
      >
        {deleting
          ? 'Deleting…'
          : 'Delete'}
      </button>
    )}
  </div>
)}




        </div>
      </div>

      {replyCount > 0 && (
        <div className="news-comment-replies-section">
          <button
            type="button"
            className="news-comment-replies-toggle"
            onClick={() =>
              setRepliesExpanded(
                (current) =>
                  !current
              )
            }
          >


{repliesExpanded
  ? `Hide ${replyCount} ${
      replyCount === 1
        ? 'reply'
        : 'replies'
    }`
  : `View ${replyCount} ${
      replyCount === 1
        ? 'reply'
        : 'replies'
    }`}



          </button>


{repliesExpanded &&
  replies.length > 0 && (
    <div className="news-comment-replies">
      {displayReplies.map(
        (reply) => (



<NewsReplyItem
  key={getCommentId(reply)}
  reply={reply}
  onReply={handleOpenReply}
  onDelete={onDelete}
  onReact={onReact}
  deletingId={deletingId}
  reactingId={reactingId}
  replyTargetId={replyTargetId}
/>


        )
      )}
    </div>
  )}



        </div>
      )}
    </article>
  );
}

export default NewsCommentItem;
